import { forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/context/store";
import FilterAccordion from "./FilterAccordion";
import { settlementActions } from "../context/settlement-slice";
import { Button } from "src/components";

const cnSuggestionList = [
  { cn: "With CN", value: "with" },
  { cn: "Without CN", value: "without" },
];

const shortageSuggestionList = [
  { shortage: "Without Shortage", label: "Without Shortage", value: "without" },
  { shortage: "With Shortage", label: "With Shortage", value: "with" },
];

type TabType = "EXPECTED" | "RELEASED" | "SETTLED" | "SEARCH";

interface FilterItem {
  [key: string]: any;
}

interface Props {
  type: TabType;
  onCloseClick: () => void;
  companyDetails?: FilterItem[];
}

const Divider = () => (
  <div className="w-full h-[0.0625rem] my-2 bg-[#0000001a]" />
);

const SettlementFilter = forwardRef<HTMLElement, Props>(
  ({ type, onCloseClick, companyDetails = [] }, ref) => {
    const dispatch = useDispatch();

    const { filters, settlementFiltersData } = useSelector(
      (state: RootState) => state.settlement
    );

    const currentFilters = filters[type];

    const isSearchTab = type === "SEARCH";
    const isAdvancedTab = type === "RELEASED" || type === "SETTLED";

    const updateFilter = (key: string, value: any) => {
      dispatch(
        settlementActions.setFilter({
          tab: type,
          key,
          value,
        })
      );
    };

    const resetFilter = () => {
      dispatch(settlementActions.resetFilters(type));
    };

    /**
     * Cleaner mapping for API filter data
     */
    const mapStockistDisplay = (list: FilterItem[] = []) =>
      list.map((item) => ({
        ...item,
        stockist_display_name: [
          item?.firm_name,
          item?.medvol_code,
          item?.stockist_company_lnk?.[0]?.compStockistCode,
        ]
          .filter(Boolean)
          .join(" - "),
      }));

    const filterDataMap: Record<TabType, any> = {
      EXPECTED: {
        stockist: mapStockistDisplay(settlementFiltersData?.expectedStockist),
        division: settlementFiltersData?.expectedDivision || [],
        product: settlementFiltersData?.expectedProduct || [],
      },
      RELEASED: {
        stockist: mapStockistDisplay(settlementFiltersData?.releasedStockist),
        division: settlementFiltersData?.releasedDivision || [],
        product: settlementFiltersData?.releasedProduct || [],
        cn: cnSuggestionList || [],
        shortage: shortageSuggestionList || [],
      },
      SETTLED: {
        stockist: mapStockistDisplay(settlementFiltersData?.settledStockist),
        division: settlementFiltersData?.settledDivision || [],
        product: settlementFiltersData?.settledProduct || [],
        cn: cnSuggestionList || [],
        shortage: shortageSuggestionList || [],
      },
      SEARCH: {
        stockist: mapStockistDisplay(settlementFiltersData?.searchStockist),
      },
    };

    const tabFilters = filterDataMap[type];

    return (
      <aside
        className="bg-white border-l min-w-[20rem] max-w-[25rem] fixed px-6 py-5 right-0 h-screen overflow-y-auto"
        ref={ref}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Fetch filters</h3>
          <Button
            className="border py-1 bg-[#7F56D9] text-white"
            onClick={resetFilter}
          >
            Reset
          </Button>
        </div>

        <div className="flex flex-col">
          {/* Company */}
          <FilterAccordion
            title="Company"
            uniqueKey="code"
            displayKey="shortname"
            defaultValue={currentFilters?.company}
            arrayToFilter={companyDetails}
            onSelectingFilter={(v) => updateFilter("company", v)}
          />

          <Divider />

          {/* Stockist */}
          <FilterAccordion
            title="Stockist"
            uniqueKey="uid"
            displayKey="stockist_display_name"
            defaultValue={currentFilters?.stockist}
            arrayToFilter={tabFilters.stockist}
            onSelectingFilter={(v) => updateFilter("stockist", v)}
            isSingleSelect  //M10-13804
          />

          {/* SEARCH TAB ENDS HERE */}
          {!isSearchTab && (
            <>
              <Divider />

              {/* Division */}
              <FilterAccordion
                title="Division"
                uniqueKey="company_divisioncode"
                displayKey="division_name"
                defaultValue={currentFilters?.division}
                arrayToFilter={tabFilters.division}
                onSelectingFilter={(v) => updateFilter("division", v)}
              />

              <Divider />

              {/* Product */}
              <FilterAccordion
                title="Product"
                uniqueKey="company_productcode"
                displayKey="name"
                defaultValue={currentFilters?.product}
                arrayToFilter={tabFilters.product}
                onSelectingFilter={(v) => updateFilter("product", v)}
              />
            </>
          )}

          {/* Advanced filters */}
          {isAdvancedTab && (
            <>
              <Divider />

              <FilterAccordion
                title="CN"
                uniqueKey="cn"
                displayKey="cn"
                defaultValue={currentFilters?.cn}
                arrayToFilter={tabFilters.cn}
                onSelectingFilter={(v) => updateFilter("cn", v)}
              />

              <Divider />

              <FilterAccordion
                title="Shortage"
                uniqueKey="shortage"
                displayKey="label"
                defaultValue={currentFilters?.shortage}
                arrayToFilter={tabFilters.shortage}
                onSelectingFilter={(v) => updateFilter("shortage", v)}
              />

              <Divider />

              <div className="py-3">
                <h4 className="text-sm font-medium mb-2">Date Range</h4>

                <div className="flex gap-3">
                  <input
                    type="date"
                    className="border rounded-md px-3 py-1.5 text-sm h-9 w-full"
                    value={currentFilters?.dateRange?.from || ""}
                    onChange={(e) =>
                      updateFilter("dateRange", {
                        ...currentFilters?.dateRange,
                        from: e.target.value,
                      })
                    }
                  />

                  <input
                    type="date"
                    className="border rounded-md px-3 py-1.5 text-sm h-9 w-full"
                    value={currentFilters?.dateRange?.to || ""}
                    onChange={(e) =>
                      updateFilter("dateRange", {
                        ...currentFilters?.dateRange,
                        to: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button className="border" onClick={onCloseClick}>
            Close
          </Button>
        </div>
      </aside>
    );
  }
);

export default SettlementFilter;
